import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../authToken';
import { API_BASE_URL } from '../config';
import {
  CASHFLOW_PAYMENT_TYPES,
  CASHFLOW_PERIODS,
  formatMoney,
  normalizeCashflow,
} from './cashflowUtils';

function getNumberStyle(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  return {
    color: n > 0 ? '#c00' : '#0a0',
    fontWeight: 800,
  };
}

function safeUpper(x) {
  return String(x ?? '').trim().toUpperCase();
}

export default function CashflowExpensePanel({ onClose }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [menuExpenseId, setMenuExpenseId] = useState(null);
  const [confirmDeleteExpenseId, setConfirmDeleteExpenseId] = useState(null);
  const [excludedExpenseIds, setExcludedExpenseIds] = useState(() => new Set());

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addName, setAddName] = useState('');
  const [addAmountPerPeriod, setAddAmountPerPeriod] = useState('');
  const [addPeriod, setAddPeriod] = useState('MONTHLY');
  const [addPaidWith, setAddPaidWith] = useState('CARD');
  const [addError, setAddError] = useState(null);
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Summary controls
  const [sortBy, setSortBy] = useState('NAME'); // NAME | AMOUNT | PER_DAY
  const [sortDir, setSortDir] = useState('ASC'); // ASC | DESC
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [paidWithFilter, setPaidWithFilter] = useState('');

  const fetchExpenses = () => {
    setLoading(true);
    setError(null);

    // Assumption: backend endpoint mirrors `BalanceAccountPanel` naming.
    // If your API uses a different path, we can adjust quickly.
    fetch(`${API_BASE_URL}/cashflowexpenses`, {
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch expenses');
        return res.json();
      })
      .then((data) => {
        setExpenses(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to fetch expenses');
        setExpenses([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enrichedExpenses = useMemo(() => {
    return (expenses || []).map((e) => {
      const stats = normalizeCashflow(e?.amountPerPeriod, e?.period);
      return {
        ...e,
        __stats: stats,
        __nameNorm: String(e?.name ?? '').trim().toLowerCase(),
        __paidWithNorm: safeUpper(e?.paidWith),
        __periodNorm: safeUpper(e?.period),
      };
    });
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    const q = String(search ?? '').trim().toLowerCase();
    const period = safeUpper(periodFilter);
    const paid = safeUpper(paidWithFilter);

    return enrichedExpenses.filter((e) => {
      if (q && !e.__nameNorm.includes(q)) return false;
      if (period && e.__periodNorm !== period) return false;
      if (paid && e.__paidWithNorm !== paid) return false;
      return true;
    });
  }, [enrichedExpenses, paidWithFilter, periodFilter, search]);

  const sortedExpenses = useMemo(() => {
    const dir = sortDir === 'DESC' ? -1 : 1;

    const keyFn = (e) => {
      if (sortBy === 'AMOUNT') return Number(e?.amountPerPeriod) || 0;
      if (sortBy === 'PER_DAY') return Number(e?.__stats?.perDay) || 0;
      // NAME
      return String(e?.name ?? '').toLowerCase();
    };

    return [...filteredExpenses].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      if (typeof ka === 'string' || typeof kb === 'string') {
        return String(ka).localeCompare(String(kb)) * dir;
      }
      return (ka - kb) * dir;
    });
  }, [filteredExpenses, sortBy, sortDir]);

  const totals = useMemo(() => {
    return filteredExpenses.reduce(
      (acc, e) => {
        if (e?.id != null && excludedExpenseIds.has(e.id)) return acc;
        const s = e?.__stats;
        acc.perDay += Number(s?.perDay) || 0;
        acc.perWeek += Number(s?.perWeek) || 0;
        acc.perMonth += Number(s?.perMonth) || 0;
        acc.perYear += Number(s?.perYear) || 0;
        return acc;
      },
      { perDay: 0, perWeek: 0, perMonth: 0, perYear: 0 }
    );
  }, [filteredExpenses, excludedExpenseIds]);

  const deleteExpense = (id) => {
    if (id == null) return;

    fetch(`${API_BASE_URL}/cashflowexpenses/${id}`, {
      method: 'DELETE',
      headers: {
        ...(authToken ? { 'Authorization': authToken } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to delete expense');
      })
      .then(() => {
        setConfirmDeleteExpenseId(null);
        setMenuExpenseId(null);
        setExcludedExpenseIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        fetchExpenses();
      })
      .catch((e) => setError(e?.message || 'Failed to delete expense'));
  };

  const toggleExcluded = (id) => {
    if (id == null) return;
    setExcludedExpenseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addExpense = () => {
    setAddError(null);

    const name = String(addName || '').trim();
    const amountNum = Number(addAmountPerPeriod);
    const period = safeUpper(addPeriod);
    const paidWith = safeUpper(addPaidWith);

    if (!name) {
      setAddError('Name is required.');
      return;
    }
    if (!Number.isFinite(amountNum) || !Number.isInteger(amountNum)) {
      setAddError('Amount must be an integer.');
      return;
    }
    if (!CASHFLOW_PERIODS.includes(period)) {
      setAddError('Invalid period.');
      return;
    }
    if (!CASHFLOW_PAYMENT_TYPES.includes(paidWith)) {
      setAddError('Invalid payment type.');
      return;
    }

    setAddSubmitting(true);
    fetch(`${API_BASE_URL}/cashflowexpenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': authToken } : {}),
      },
      body: JSON.stringify({
        name,
        amountPerPeriod: amountNum,
        period,
        paidWith,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to add expense');
        return res.json().catch(() => null);
      })
      .then(() => {
        setShowAddPanel(false);
        setAddName('');
        setAddAmountPerPeriod('');
        setAddPeriod('MONTHLY');
        setAddPaidWith('CARD');
        fetchExpenses();
      })
      .catch((e) => setAddError(e?.message || 'Failed to add expense'))
      .finally(() => setAddSubmitting(false));
  };

  const prettyStats = (s) => {
    return `${formatMoney(s?.perDay)}/D, ${formatMoney(s?.perWeek)}/W, ${formatMoney(s?.perMonth)}/M, ${formatMoney(s?.perYear)}/Y`;
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: '24px 12px 18px 12px',
        boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        minWidth: 0,
        width: '980px',
        maxWidth: '98vw',
        minHeight: 0,
        height: '720px',
        maxHeight: '96vh',
        overflow: 'auto',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          fontSize: 24,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
        aria-label="Close"
      >
        ✖️
      </button>

      <h2 style={{ marginBottom: 12 }}>Cashflow – Expenses</h2>

      {/* Summary + controls */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginBottom: 16,
          background: '#eef8ff',
          borderRadius: 10,
          padding: '12px 14px',
          border: '1px solid #09f',
        }}
      >
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>
            Items: <b>{filteredExpenses.length}</b>
          </span>
          <span>
            Total /day: <b style={getNumberStyle(totals.perDay)}>{formatMoney(totals.perDay)}</b>
          </span>
          <span>
            /week: <b style={getNumberStyle(totals.perWeek)}>{formatMoney(totals.perWeek)}</b>
          </span>
          <span>
            /month: <b style={getNumberStyle(totals.perMonth)}>{formatMoney(totals.perMonth)}</b>
          </span>
          <span>
            /year: <b style={getNumberStyle(totals.perYear)}>{formatMoney(totals.perYear)}</b>
          </span>

          <div style={{ flex: 1 }} />
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name…"
            style={{
              flex: '1 1 220px',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #bcd',
              fontSize: '1rem',
            }}
          />

          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #bcd',
              fontSize: '1rem',
              background: '#fff',
            }}
            title="Filter by period"
          >
            <option value="">All periods</option>
            {CASHFLOW_PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            value={paidWithFilter}
            onChange={(e) => setPaidWithFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #bcd',
              fontSize: '1rem',
              background: '#fff',
            }}
            title="Filter by payment type"
          >
            <option value="">All payment types</option>
            {CASHFLOW_PAYMENT_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #bcd',
              fontSize: '1rem',
              background: '#fff',
            }}
            title="Sort by"
          >
            <option value="NAME">Sort: Name</option>
            <option value="AMOUNT">Sort: Amount/period</option>
            <option value="PER_DAY">Sort: /day</option>
          </select>

          <button
            style={{
              padding: '10px 10px',
              borderRadius: 8,
              border: '1px solid #bcd',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 900,
              lineHeight: 1,
              minWidth: 42,
            }}
            onClick={() => setSortDir((d) => (d === 'DESC' ? 'ASC' : 'DESC'))}
            title={sortDir === 'DESC' ? 'Sort direction: DESC (click to switch to ASC)' : 'Sort direction: ASC (click to switch to DESC)'}
            aria-label="Toggle sort direction"
          >
            {sortDir === 'DESC' ? '↓' : '↑'}
          </button>

          <div style={{ flex: 1 }} />

          <button
            style={{
              padding: '10px 10px',
              borderRadius: 8,
              border: '1px solid #09f',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 900,
              color: '#036',
              lineHeight: 1,
              minWidth: 42,
            }}
            onClick={fetchExpenses}
            title="Refresh"
            aria-label="Refresh"
          >
            ↻
          </button>

          <button
            style={{
              padding: '10px 10px',
              borderRadius: 8,
              border: '1px solid #bcd',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 900,
              lineHeight: 1,
              minWidth: 42,
            }}
            onClick={() => {
              setSearch('');
              setPeriodFilter('');
              setPaidWithFilter('');
              setSortBy('NAME');
              setSortDir('ASC');
            }}
            title="Reset filters"
            aria-label="Reset filters"
          >
            ⟲
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #09f',
              background: '#eef8ff',
              cursor: 'pointer',
              fontWeight: 900,
              color: '#036',
            }}
            onClick={() => setShowAddPanel(true)}
            title="Add new expense"
          >
            ➕ Add
          </button>
        </div>
      </div>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {sortedExpenses.map((e) => {
          const key = e?.id ?? `${e?.name}-${e?.amountPerPeriod}-${e?.period}-${e?.paidWith}`;
          const s = e?.__stats;
          const isExcluded = e?.id != null && excludedExpenseIds.has(e.id);

          return (
            <div
              key={key}
              style={{
                border: '1px solid #eee',
                borderRadius: 10,
                padding: '10px 12px',
                background: isExcluded ? '#fafafa' : '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'stretch',
                gap: 10,
                opacity: isExcluded ? 0.6 : 1,
                position: 'relative',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* line 1 */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                  <div style={{ flex: 1, fontSize: '1.05rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e?.name}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, textAlign: 'right' }}>
                    <span style={getNumberStyle(e?.amountPerPeriod)}>{e?.amountPerPeriod}</span>
                  </div>
                </div>

                {/* line 2 */}
                <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: '#666' }}>
                  <div style={{ flex: 1 }}>{safeUpper(e?.period) || '-'} · {safeUpper(e?.paidWith) || '-'}</div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{prettyStats(s)}</div>
                </div>
              </div>

              {/* rightmost menu placeholder */}
              <div style={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 22,
                    cursor: 'pointer',
                    color: '#888',
                    padding: 0,
                    lineHeight: 1,
                  }}
                  aria-label="Menu"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setMenuExpenseId((cur) => (cur === e?.id ? null : e?.id));
                  }}
                >
                  ⋯
                </button>

                {menuExpenseId === e?.id && (
                  <div
                    style={{
                      position: 'absolute',
                      background: '#fff',
                      border: '1px solid #eee',
                      borderRadius: 8,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                      padding: 12,
                      zIndex: 12000,
                      right: 24,
                      marginTop: 30,
                      minWidth: 190,
                    }}
                  >
                    <button
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 0',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#036',
                        fontWeight: 700,
                        textAlign: 'left',
                      }}
                      onClick={() => {
                        toggleExcluded(e?.id);
                        setMenuExpenseId(null);
                      }}
                    >
                      {isExcluded ? 'Include in stats' : 'Exclude from stats'}
                    </button>
                    <button
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 0',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#a22',
                        fontWeight: 700,
                        textAlign: 'left',
                      }}
                      onClick={() => {
                        setMenuExpenseId(null);
                        setConfirmDeleteExpenseId(e?.id);
                      }}
                    >
                      Delete
                    </button>
                    <button
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 0',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#888',
                        textAlign: 'left',
                      }}
                      onClick={() => setMenuExpenseId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {!loading && sortedExpenses.length === 0 && (
          <div style={{ padding: '16px 8px', color: '#666' }}>No items.</div>
        )}
      </div>

      {confirmDeleteExpenseId != null && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
            padding: 28,
            zIndex: 13000,
            minWidth: 260,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 14,
          }}
        >
          <div style={{ fontSize: '1.05rem', fontWeight: 800 }}>Delete expense?</div>
          <div style={{ color: '#666', fontSize: 13 }}>This cannot be undone.</div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
              }}
              onClick={() => setConfirmDeleteExpenseId(null)}
            >
              Cancel
            </button>
            <button
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #a22',
                background: '#a22',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 800,
              }}
              onClick={() => deleteExpense(confirmDeleteExpenseId)}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {showAddPanel && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
            padding: 22,
            zIndex: 13000,
            width: 420,
            maxWidth: '92vw',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: '1.1rem', fontWeight: 900 }}>Add expense</div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>Name</span>
            <input
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="e.g. Rent"
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ccc', fontSize: 14 }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>Amount per period (integer)</span>
            <input
              value={addAmountPerPeriod}
              onChange={(e) => setAddAmountPerPeriod(e.target.value)}
              inputMode="numeric"
              placeholder="e.g. 2500"
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ccc', fontSize: 14 }}
            />
          </label>

          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <span style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>Period</span>
              <select
                value={addPeriod}
                onChange={(e) => setAddPeriod(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ccc', background: '#fff', fontSize: 14 }}
              >
                {CASHFLOW_PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <span style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>Paid with</span>
              <select
                value={addPaidWith}
                onChange={(e) => setAddPaidWith(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ccc', background: '#fff', fontSize: 14 }}
              >
                {CASHFLOW_PAYMENT_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {addError && <div style={{ color: '#a22', fontWeight: 700 }}>{addError}</div>}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
              }}
              onClick={() => {
                setShowAddPanel(false);
                setAddError(null);
              }}
              disabled={addSubmitting}
            >
              Cancel
            </button>
            <button
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #09f',
                background: '#09f',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 900,
              }}
              onClick={addExpense}
              disabled={addSubmitting}
            >
              {addSubmitting ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: '#666', marginTop: 'auto' }}>
        Period normalization: month=30d, year=365d.
      </div>
    </div>
  );
}
